import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoansPage() {
  const [loans, setLoans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const { t } = useLanguage();
  const { isHR, isAdmin } = useAuth();
  
  const [form, setForm] = useState({
    employee_id: '',
    total_amount: 0,
    monthly_rate: 0,
    start_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [loansRes, employeesRes] = await Promise.all([
          axios.get(`${API}/loans`),
          axios.get(`${API}/employees`, { params: { is_active: true } })
        ]);
        setLoans(loansRes.data);
        setEmployees(employeesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const refetchData = async () => {
    try {
      const [loansRes, employeesRes] = await Promise.all([
        axios.get(`${API}/loans`),
        axios.get(`${API}/employees`, { params: { is_active: true } })
      ]);
      setLoans(loansRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/loans`, {
        ...form,
        total_amount: parseFloat(form.total_amount),
        monthly_rate: parseFloat(form.monthly_rate)
      });
      toast.success(t('savedSuccessfully'));
      setDialogOpen(false);
      setForm({ employee_id: '', total_amount: 0, monthly_rate: 0, start_date: new Date().toISOString().split('T')[0], notes: '' });
      refetchData();
    } catch (error) {
      console.error('Error creating loan:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handlePayment = async () => {
    try {
      await axios.put(`${API}/loans/${selectedLoan.id}/payment`, { amount: parseFloat(paymentAmount) });
      toast.success(t('savedSuccessfully'));
      setPaymentDialogOpen(false);
      setSelectedLoan(null);
      setPaymentAmount(0);
      refetchData();
    } catch (error) {
      console.error('Error recording payment:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = t('errorOccurred');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : empId;
  };

  const handleDelete = async (loanId) => {
    if (!window.confirm(t('confirmDeleteLoan'))) return;
    
    try {
      await axios.delete(`${API}/loans/${loanId}`);
      toast.success(t('deletedSuccessfully'));
      refetchData();
    } catch (error) {
      console.error('Error deleting loan:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value || 0) + ' TND';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="loans-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('loans')}</h1>
        {isHR() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A] btn-primary-glow" data-testid="add-loan-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('newLoan')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-[#1A1A1A]">{t('newLoan')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('employees')}</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm(prev => ({ ...prev, employee_id: v }))}>
                    <SelectTrigger className="bg-white border-gray-200 text-[#1A1A1A]">
                      <SelectValue placeholder={t('employees')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('totalAmount')}</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={form.total_amount}
                    onChange={(e) => setForm(prev => ({ ...prev, total_amount: e.target.value }))}
                    className="bg-white border-gray-200 text-[#1A1A1A]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('monthlyRate')}</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={form.monthly_rate}
                    onChange={(e) => setForm(prev => ({ ...prev, monthly_rate: e.target.value }))}
                    className="bg-white border-gray-200 text-[#1A1A1A]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('startDate')}</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                    className="bg-white border-gray-200 text-[#1A1A1A]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('notes')}</Label>
                  <Input
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="bg-white border-gray-200 text-[#1A1A1A]"
                  />
                </div>
                <Button type="submit" className="w-full bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A]">
                  {t('save')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="table-header">{t('employees')}</TableHead>
                  <TableHead className="table-header">{t('startDate')}</TableHead>
                  <TableHead className="table-header">{t('totalAmount')}</TableHead>
                  <TableHead className="table-header">{t('monthlyRate')}</TableHead>
                  <TableHead className="table-header">{t('remainingAmount')}</TableHead>
                  <TableHead className="table-header">{t('status')}</TableHead>
                  <TableHead className="table-header text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-400">
                      {t('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  loans.map((loan) => (
                    <TableRow key={loan.id} className="table-row" data-testid={`loan-row-${loan.id}`}>
                      <TableCell className="text-[#1A1A1A]">{getEmployeeName(loan.employee_id)}</TableCell>
                      <TableCell className="text-gray-500 font-mono">{loan.start_date}</TableCell>
                      <TableCell className="font-mono text-[#1A1A1A]">{formatCurrency(loan.total_amount)}</TableCell>
                      <TableCell className="font-mono text-blue-400">{formatCurrency(loan.monthly_rate)}</TableCell>
                      <TableCell className="font-mono text-purple-400">{formatCurrency(loan.remaining_amount)}</TableCell>
                      <TableCell>
                        {loan.status === 'closed' ? (
                          <Badge className="badge-success">{t('closed')}</Badge>
                        ) : (
                          <Badge className="badge-info">{t('active')}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {loan.status === 'active' && isHR() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedLoan(loan);
                                setPaymentAmount(loan.monthly_rate);
                                setPaymentDialogOpen(true);
                              }}
                              className="text-purple-400 hover:text-purple-300"
                              data-testid={`payment-loan-${loan.id}`}
                            >
                              <Minus className="w-4 h-4 mr-1" />
                              {t('payment')}
                            </Button>
                          )}
                          {isAdmin() && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(loan.id)}
                              className="text-gray-500 hover:text-[#E31E24]"
                              data-testid={`delete-loan-${loan.id}`}
                              title={t('deleteOnlyAdmin')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-[#1A1A1A]">{t('payment')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-500">
              {t('remainingAmount')}: {formatCurrency(selectedLoan?.remaining_amount)}
            </p>
            <p className="text-gray-500">
              {t('monthlyRate')}: {formatCurrency(selectedLoan?.monthly_rate)}
            </p>
            <div className="space-y-2">
              <Label className="text-gray-500">{t('amount')}</Label>
              <Input
                type="number"
                step="0.001"
                max={selectedLoan?.remaining_amount}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="bg-white border-gray-200 text-[#1A1A1A]"
              />
            </div>
            <Button onClick={handlePayment} className="w-full bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A]">
              {t('confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
